import React from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { countryNameFor } from '../../util/custom/countryNames';
import {
  getStockLocations,
  getActiveLocations,
  findLocationById,
} from '../../util/custom/stockLocations';
import FieldSelect from '../FieldSelect/FieldSelect';

import css from './StockLocationSelect.module.css';

export const ADD_NEW_LOCATION_VALUE = '__add-new__';

/**
 * "Main shop — San Francisco, United States". The city is the most useful
 * middle part; the region stands in when the geocoder gave no city, and the
 * country alone is enough when it gave neither (EC8).
 */
export const optionLabelFor = (location, locale) => {
  const place = [location?.city || location?.region, countryNameFor(location?.countryCode, locale)]
    .filter(Boolean)
    .join(', ');
  return place ? `${location.name} — ${place}` : location.name;
};

/**
 * The location picker (CL-STOCK-01 section H).
 *
 * Reads the seller's address book straight from Redux, so no base-template file
 * has to thread a new prop through the wizard.
 *
 * @param {Object} props
 * @param {string} props.name - the Final Form field name
 * @param {string} props.id
 * @param {Function} props.onAddNew - opens the add-location modal
 * @param {string} [props.currentValue] - the id the listing currently holds
 */
const StockLocationSelect = props => {
  const { className, rootClassName, name, id, onAddNew, currentValue = null } = props;
  const intl = useIntl();
  const form = useForm();

  const currentUser = useSelector(state => state.user.currentUser);
  const active = getActiveLocations(getStockLocations(currentUser));

  // EC14: a listing may point at a location the seller has since deleted. That
  // is normal under D3 — the listing keeps its own copy — so never throw, and
  // tell the seller what happened.
  const valueIsMissing = !!currentValue && !findLocationById(active, currentValue);

  // NOTE the signature: FieldSelect calls onChange with the VALUE, not the event
  // (FieldSelect.js:42 — `onChange(e.currentTarget.value)`). Reading
  // `event.target.value` here would throw on a string.
  const handleChange = value => {
    if (value === ADD_NEW_LOCATION_VALUE) {
      // Put the field back where it was, then open the modal: an "add new"
      // sentinel must never be saved onto a listing.
      form.change(name, currentValue || undefined);
      onAddNew();
    }
  };

  return (
    <div className={classNames(rootClassName || css.root, className)}>
      <FieldSelect
        id={id}
        name={name}
        label={intl.formatMessage({ id: 'StockLocationSelect.label' })}
        onChange={handleChange}
      >
        <option value="">{intl.formatMessage({ id: 'StockLocationSelect.placeholder' })}</option>
        {active.map(location => (
          <option key={location.id} value={location.id}>
            {optionLabelFor(location, intl.locale)}
          </option>
        ))}
        <option value={ADD_NEW_LOCATION_VALUE}>
          {intl.formatMessage({ id: 'StockLocationSelect.addNewOption' })}
        </option>
      </FieldSelect>

      {valueIsMissing ? (
        <p className={css.missing}>
          <FormattedMessage id="StockLocationSelect.missingLocation" />
        </p>
      ) : null}

      {/* G8 / SL8: the one sentence that carries decision D3. */}
      <p className={css.notice}>
        <FormattedMessage id="StockLocationSelect.shortcutNotice" />
      </p>
    </div>
  );
};

export default StockLocationSelect;
